/** @format */

import {
	APP_ID,
	ErrorHandler,
	inject,
	ModuleWithProviders,
	NgModule,
	provideAppInitializer
} from '@angular/core';
import {
	BrowserModule,
	provideClientHydration,
	withNoHttpTransferCache
} from '@angular/platform-browser';
import { Chain, Environment, LocalStorageConfig } from '@shared/data-access/interfaces';
import {
	CustomUrlSerializer,
	TaggingService,
	WindowModule,
	KillswitchService,
	LoggerConfig
} from '@shared/data-access/services';
import { DCSG_APP_NAME, GlobalErrorHandler, LogDataAccessModule } from '@logger';
import { RouterModule, UrlSerializer } from '@angular/router';
import { routerReducer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { commonReducer, homrReducer } from '@state/data-access';

import { APP_BASE_HREF } from '@angular/common';
import { AUTH_URL_FILTER } from '@shared/authentication/data-access';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CeopsComponent } from '@shared/ceops';
import { FeedbackModule } from '@pdp/components';
import { HTTP_INTERCEPTOR_PROVIDERS } from './app.interceptors';
import { HttpClientModule } from '@angular/common/http';
import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { ProductAppRoutingModule } from './app-routing.module';
import { ProductModule } from './product/product.module';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { Store, StoreModule } from '@ngrx/store';
import { chain } from '@core/chain';
import { environment } from '@environment/pdp';
import { appInitializerKillswitchesSet, headerLocationReducer } from '@pdp/data-access/state';
import { take } from 'rxjs/operators';

const LOCAL_STORAGE_CONFIG: LocalStorageConfig = {
	prefix: ''
};

const LOGGER_CONFIG: LoggerConfig = {
	clientLoggerLevel: environment.logging.client.level,
	xhrLoggerLevel: environment.logging.xhr.level,
	xhrLoggerUrl: `${environment.api.spring.microServiceProxy.logger.path}publish`
};

/*
 * Module Imports
 */
const IMPORTS = [
	BrowserAnimationsModule,
	BrowserModule,
	CeopsComponent,
	WindowModule.forRoot(LOCAL_STORAGE_CONFIG),
	LogDataAccessModule.forRoot(LOGGER_CONFIG),
	FeedbackModule,
	HttpClientModule,
	ProductModule,
	StoreModule.forRoot({
		common: commonReducer,
		homr: homrReducer,
		headerLocation: headerLocationReducer,
		router: routerReducer
	}),
	StoreRouterConnectingModule.forRoot(),
	StoreDevtoolsModule.instrument({
		maxAge: 25,
		logOnly: environment.production,
		connectInZone: true
	})
];

/*
 * Module Providers
 */
const PROVIDERS = [
	{ provide: APP_ID, useValue: 'dcsg-ngx-pdp-server' },
	{ provide: APP_BASE_HREF, useValue: '/' },
	{
		provide: AUTH_URL_FILTER,
		useValue: [
			environment.baseUrls.pointService,
			environment.baseUrls.identityService,
			environment.baseUrls.athleteService,
			environment.api.myLocker
		]
	},
	/*
	 * APP_INITIALIZER InjectionToken is used to perform any logic during app initialization,
	 * this would execute before AppComponent or any Resolve Guards.  This guarantees anything
	 * required for setup is completed ahead of time.
	 */
	provideAppInitializer(() => {
		const initializerFn = (
			(service: ProductAnalyticsService) => () =>
				service.resetAnalytics()
		)(inject(ProductAnalyticsService));
		return initializerFn();
	}),
	provideAppInitializer(() => {
		const killswitchService = inject(KillswitchService);
		const store = inject(Store);
		killswitchService
			.getAllKillSwitchesForCurrentChain$()
			.pipe(take(1))
			.subscribe((killswitches) => {
				store.dispatch(appInitializerKillswitchesSet({ killswitches }));
			});
	}),
	{ provide: Chain, useValue: chain || {} },
	{ provide: DCSG_APP_NAME, useValue: 'PDP' },
	{ provide: Environment, useValue: environment || {} },
	{ provide: ErrorHandler, useClass: GlobalErrorHandler },
	...HTTP_INTERCEPTOR_PROVIDERS,
	{ provide: UrlSerializer, useClass: CustomUrlSerializer },
	TaggingService,
	provideClientHydration(withNoHttpTransferCache())
];

/**
 * NOTE: This "common" module is used to define the AppComponent since it can only be declared
 * in a single module but is needed in more than one.
 */
@NgModule({
	declarations: [AppComponent],
	imports: [...IMPORTS, RouterModule],
	exports: [AppComponent, RouterModule]
})
export class ProductCommonAppModule {}

/**
 * NOTE: This is the main product app module and is for the standalone product app and does include the router
 */
@NgModule({
	imports: [...IMPORTS, ProductAppRoutingModule, ProductCommonAppModule],
	providers: PROVIDERS,
	bootstrap: [AppComponent]
})
export class ProductAppModule {}

/**
 * NOTE: This module has the same imports and providers as the "main" standalone product app with the
 * exception to the router module.  The child product app should not have the router included.
 */
@NgModule({
	imports: [...IMPORTS, ProductCommonAppModule],
	providers: PROVIDERS
})
export class ProductChildAppModule {}

@NgModule({})
export class ProductSharedAppModule {
	static forRoot(): ModuleWithProviders<ProductChildAppModule> {
		return {
			ngModule: ProductChildAppModule,
			providers: PROVIDERS
		};
	}
}
